import { Avatar, Box, Divider, Flex, HStack, VStack } from "@chakra-ui/react";
import RecipientTag from "components/Tag/ResourceTargetTag/RecipientTag";
import { ShowName } from "features/Resources/Shared/Components/Fields/NameField";
import EmbeddedHtml from "components/DataDisplay/EmbeddedHtml";
import { ActionTypeTag } from "design-system";
import ComposeBox, { useComposeBoxColors } from "features/Resources/Shared/Components/ComposeBox";
import moment from "moment-timezone";
import { titleize } from "helpers/string";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";
import FailedActionBanner from "features/Resources/Shared/Components/FailedActionBanner";
import FailedActionHeaderButtons from "features/Resources/Shared/Components/FailedActionHeaderButtons";
import CDS from "design-system";
import { useUserDetailQuery } from "features/User/hooks";
import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";
import { Message } from "../models";

const MESSAGE_STATUS_COLORS: Record<string, string> = {
  pending: "blue.400",
  delivered: "green.500",
  failed: "red.500",
  skipped: "gray.400",
  removed: "gray.400",
};

const MESSAGE_STATUS_LABELS: Record<string, string> = {
  pending: "Unsent",
  delivered: "Delivered",
  failed: "Failed",
  skipped: "Skipped",
  removed: "Removed",
};

function SentAtFieldTag({ message }: { message: Message }) {
  const isDelivered = message.status === "delivered";
  const label = isDelivered ? "Sent" : "Scheduled";
  const date = isDelivered ? message.deliveredAt : message.deliverAt;

  if (!date) return null;
  const timezone = message.timezone || moment.tz.guess();
  return <CDS.FieldTag label={label}>{moment.tz(date, timezone).format("MMM D, YYYY [at] h:mm A")}</CDS.FieldTag>;
}

// Read-only sender chip mirroring MessageForm's SenderSelect trigger in its disabled state —
// a small bordered card with avatar + name, no chevron, no popover.
function StaticSenderChip({ senderId, bg }: { senderId?: string | null; bg: string }) {
  const { data: sender } = useUserDetailQuery(senderId || "", { enabled: !!senderId });
  const isBot = !senderId;
  const displayName = sender?.fullName ?? "Camino Bot";
  return (
    <HStack
      spacing={2}
      px={3}
      py={1.5}
      borderWidth={1}
      borderColor="gray.200"
      borderRadius="md"
      bg={bg}
      cursor="default"
      minW="220px"
    >
      <Avatar
        name={displayName}
        src={isBot ? undefined : (sender?.avatarUrl ?? undefined)}
        size="xs"
        flexShrink={0}
      />
      <CDS.Text12 fontWeight="medium" color="content.primary" flex={1} noOfLines={1}>
        {displayName}
      </CDS.Text12>
    </HStack>
  );
}

// "Delivered Jun 17, 2026 at 12:51 PM" style line shown inside the compose body —
// helps anchor the message to when it was actually sent (or skipped/removed/failed).
function statusFooterText(message: Message): string | null {
  const timezone = message.timezone || moment.tz.guess();
  const fmt = (iso?: string | Date | null) => (iso ? moment.tz(iso, timezone).format("MMM D, YYYY [at] h:mm A") : null);

  switch (message.status) {
    case "pending":
      return null;
    case "delivered": {
      const at = fmt(message.effectiveDeliverAt) ?? fmt(message.deliveredAt) ?? fmt(message.statusUpdatedAt);
      return at ? `Delivered ${at}` : null;
    }
    case "skipped": {
      const at = fmt(message.statusUpdatedAt);
      return at ? `Skipped ${at}` : null;
    }
    case "failed": {
      const at = fmt(message.attemptedAt) ?? fmt(message.statusUpdatedAt);
      return at ? `Failed ${at}` : null;
    }
    case "removed": {
      const at = fmt(message.statusUpdatedAt);
      return at ? `Removed ${at}` : null;
    }
    default:
      return null;
  }
}

interface SentMessageProps {
  message: Message;
  includeHeader?: boolean;
  includeStatus?: boolean;
  includeJourney?: boolean;
}

export default function SentMessage({ message, includeHeader = true, includeJourney = true }: SentMessageProps) {
  const isFailed = message?.status === "failed";
  const { bodyBg } = useComposeBoxColors();
  const footerText = statusFooterText(message);
  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" mb={2}>
          <HStack align="center">
            <ActionTypeTag.MessageBadge />
            <ShowName name={message?.name} />
          </HStack>
          <HStack spacing={2}>
            {isFailed && <FailedActionHeaderButtons prefixId={message.prefixId} />}
            {message && <ResourceOptionMenu prefixId={message?.prefixId} includeTemplate />}
          </HStack>
        </HStack>
      )}

      {/* Pill row */}
      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {/* Status */}
        <CDS.BasePill cursor="default" _hover={{}}>
          <Flex direction="column" gap="0">
            <CDS.Text10 color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Status
            </CDS.Text10>
            <HStack spacing="1" align="center">
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={MESSAGE_STATUS_COLORS[message.status] ?? "gray.400"}
                flexShrink={0}
              />
              <CDS.Text12 fontWeight="700" color="content.primary" lineHeight="1.3">
                {MESSAGE_STATUS_LABELS[message.status ?? ""] ?? titleize(message.status ?? "")}
              </CDS.Text12>
            </HStack>
          </Flex>
        </CDS.BasePill>

        {/* Sent At / Scheduled At */}
        <SentAtFieldTag message={message} />

        {/* Onboarding Journey — last in the row */}
        {includeJourney && message.newHireJourneyId && (
          <NewHireStackedLabel journeyId={message.newHireJourneyId} emphasized />
        )}
      </Flex>

      {isFailed && (
        <FailedActionBanner
          prefixId={message.prefixId}
          errorMessage={message.errorMessage}
          actionNoun="message"
        />
      )}

      <Divider my="4" />

      {/* Send from — mirrors MessageForm's labeled row, always read-only here. */}
      <HStack spacing={3} align="center" mb={3}>
        <CDS.Text12 fontWeight="medium" color="fg.muted" w="20" flexShrink={0}>
          Send from
        </CDS.Text12>
        <StaticSenderChip senderId={message.senderId} bg={bodyBg} />
      </HStack>

      <ComposeBox>
        <ComposeBox.HeaderRow label="To">
          <RecipientTag
            resourceId={message.recipientId}
            resourceType={message.recipientType}
            role={message.recipientRole}
            loadingLabel={message.recipientRole ? `${String(message.recipientRole).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} (tbd)` : undefined}
            disabled
          />
        </ComposeBox.HeaderRow>
        <Box px={4} pt={3} pb={3} bg={bodyBg}>
          <EmbeddedHtml innerHtml={message.deliveredBodyHtml ?? message.bodyDisplay ?? message.body} minH="72px" />
          {footerText && (
            <CDS.Text10 mt={3} color="content.tertiary" textAlign="right">
              {footerText}
            </CDS.Text10>
          )}
        </Box>
      </ComposeBox>
    </VStack>
  );
}
